/**
 * Paso 4 de la carga: cómo queda la base si se importa (SPEC-BD §6.2).
 *
 * Todo lo que se muestra lo calculó el backend con la misma función que la importación
 * (`previsualizar`): acá no se cuenta nada. Los teléfonos de los ejemplos y los valores de los errores ya
 * vienen enmascarados, y `destinoParaMostrar` los vuelve a enmascarar por si alguno llegara completo.
 */

import { AlertTriangle, CheckCircle2, Copy, Download, FileSpreadsheet, Loader2, PhoneOff, RefreshCw, ShieldCheck, ShieldOff, ShieldX, UserPlus, Users, XCircle } from "lucide-react";
import type { Previsualizacion } from "../../types.base";
import StatTile from "../../../campana-nueva/components/StatTile";
import { num, plural } from "../../../campana-nueva/utils/format";
import { destinoParaMostrar } from "../../logic/formato";
import { textoMotivoError, tituloMuestra } from "../../logic/base";
import { ChipConsentimiento } from "./ChipConsentimiento";

const ACCION: Record<Previsualizacion["ejemplos"][number]["accion"], { label: string; cls: string }> = {
  crear: { label: "Nuevo", cls: "bg-violet-50 text-violet-700" },
  actualizar: { label: "Se actualiza", cls: "bg-sky-50 text-sky-700" },
  sin_cambios: { label: "Sin cambios", cls: "bg-slate-100 text-slate-500" },
};

interface Props {
  previa: Previsualizacion | null;
  calculando: boolean;
  error: string;
  /** Los `errores` de un MAPEO_COLUMNAS_INVALIDO: van debajo de «Revisá las columnas:». */
  erroresMapeo: string[];
  onReintentar: () => void;
  onDescargarErrores: () => void;
  descargando: boolean;
  errorDescarga: string;
}

export function ConteosCarga({ previa, calculando, error, erroresMapeo, onReintentar, onDescargarErrores, descargando, errorDescarga }: Props) {
  if (calculando && !previa) {
    return (
      <div className="space-y-3" aria-busy="true" aria-label="Calculando">
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-3">{[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-28 rounded-2xl bg-slate-100 animate-pulse" />)}</div>
        <div className="h-40 rounded-2xl bg-slate-100 animate-pulse" />
      </div>
    );
  }
  if (error || erroresMapeo.length) {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-red-700" role="alert">
        {erroresMapeo.length ? (
          <>
            <p className="font-semibold">Revisá las columnas:</p>
            <ul className="mt-1 list-disc pl-5 space-y-0.5">{erroresMapeo.map((e) => <li key={e}>{e}</li>)}</ul>
          </>
        ) : (
          <>
            <p className="font-semibold">No pudimos revisar el archivo.</p>
            <p className="mt-1">{error}</p>
          </>
        )}
        <button type="button" onClick={onReintentar} className="mt-3 inline-flex items-center gap-1.5 font-semibold underline">
          <RefreshCw size={13} /> Reintentar
        </button>
      </div>
    );
  }
  if (!previa) return null;

  const k = previa.conteos;
  const avisoAmbiguos = k.sinCodigoDePais > 0 && k.seExcluyenAmbiguos;

  return (
    <div className={`space-y-5 ${calculando ? "opacity-60" : ""}`} aria-busy={calculando || undefined}>
      {calculando && <p className="text-xs text-slate-500 flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Recalculando…</p>}

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-3">
        <StatTile icon={<FileSpreadsheet size={16} />} label="Filas leídas" value={num(k.filas)} />
        <StatTile icon={<UserPlus size={16} />} label="Contactos nuevos" value={num(k.nuevos)} tono="respuesta" />
        <StatTile icon={<Users size={16} />} label="Ya estaban (se actualizan)" value={num(k.actualizados)} tono="lectura" />
        <StatTile icon={<CheckCircle2 size={16} />} label="Sin cambios" value={num(k.sinCambios)} />
        <StatTile icon={<Copy size={16} />} label="Repetidos en el archivo" value={num(k.duplicadosEnArchivo)} />
        <StatTile icon={<XCircle size={16} />} label="Teléfonos inválidos (no se importan)" value={num(k.telefonosInvalidos)} tono={k.telefonosInvalidos ? "baja" : "neutro"} />
      </div>
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatTile icon={<ShieldCheck size={16} />} label="Con consentimiento" value={num(k.conConsentimiento)} tono="resultado" />
        <StatTile icon={<ShieldOff size={16} />} label="Sin consentimiento" value={num(k.sinConsentimiento)} />
        <StatTile icon={<ShieldX size={16} />} label="Dijeron que no" value={num(k.dijeronQueNo)} tono={k.dijeronQueNo ? "baja" : "neutro"} />
        <StatTile icon={<PhoneOff size={16} />} label="En no contactar" value={num(k.enNoContactar)} tono={k.enNoContactar ? "baja" : "neutro"} />
      </div>

      {(avisoAmbiguos || previa.advertencias.length > 0) && (
        <ul className="space-y-2">
          {avisoAmbiguos && (
            <li className="flex items-start gap-2 rounded-xl bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
              <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" />
              <span>
                {`${num(k.sinCodigoDePais)} ${plural(k.sinCodigoDePais, "teléfono vino", "teléfonos vinieron")} sin código de país. Se importan, pero no van a recibir campañas mientras la marca excluya esos números (Configuración → Envío). Si son celulares argentinos, volvé a "Vincular" y marcalo.`}
              </span>
            </li>
          )}
          {previa.advertencias.map((a) => (
            <li key={a} className="flex items-start gap-2 rounded-xl bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
              <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" /><span>{a}</span>
            </li>
          ))}
        </ul>
      )}

      {previa.ejemplos.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-slate-900 mb-2">{tituloMuestra(previa.ejemplos.length, k.nuevos + k.actualizados + k.sinCambios)}</h4>
          <div className="overflow-x-auto rounded-2xl border border-slate-100">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2">Fila</th><th className="px-3 py-2">Teléfono</th><th className="px-3 py-2">Nombre</th>
                  <th className="px-3 py-2">Etiquetas</th><th className="px-3 py-2">Consentimiento</th><th className="px-3 py-2">Qué pasa</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {previa.ejemplos.map((e) => (
                  <tr key={e.fila}>
                    <td className="px-3 py-2 text-slate-500 tabular-nums">{e.fila}</td>
                    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{destinoParaMostrar(e.destino, false)}</td>
                    <td className="px-3 py-2">{e.nombre || <span className="text-slate-400">Sin nombre</span>}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{e.etiquetas.join(", ") || "—"}</td>
                    <td className="px-3 py-2"><ChipConsentimiento valor={e.consentimiento} /></td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${ACCION[e.accion]?.cls ?? "bg-slate-100 text-slate-500"}`}>
                        {ACCION[e.accion]?.label ?? e.accion}
                      </span>
                      {e.enNoContactar && <span className="ml-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-50 text-red-700">No contactar</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {previa.errores.total > 0 && (
        <div className="rounded-2xl border border-slate-100 p-4 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h4 className="text-sm font-semibold text-slate-900">{num(previa.errores.total)} {previa.errores.total === 1 ? "fila con problemas" : "filas con problemas"}</h4>
            <button type="button" onClick={onDescargarErrores} disabled={descargando}
              className="h-9 px-3 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50">
              {descargando ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Descargar el detalle (CSV)
            </button>
          </div>
          {errorDescarga && <p className="text-xs text-red-600" role="alert">{errorDescarga}</p>}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                <tr><th className="py-1.5 pr-3">Fila</th><th className="py-1.5 pr-3">Columna</th><th className="py-1.5 pr-3">Problema</th><th className="py-1.5">Valor</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {previa.errores.primeros.slice(0, 20).map((e, i) => (
                  <tr key={`${e.fila}-${e.columna}-${i}`}>
                    <td className="py-1.5 pr-3 tabular-nums text-slate-500">{e.fila}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{e.columna || "—"}</td>
                    <td className={`py-1.5 pr-3 ${e.nivel === "error" ? "text-red-700" : "text-amber-700"}`}>{textoMotivoError(e.motivo, e)}</td>
                    {/* En «duplicado» el valor es la fila con la que se unió, y ya está dicho en el problema. */}
                    <td className="py-1.5 font-mono text-xs text-slate-500 break-all">{(e.motivo !== "duplicado" && e.valor) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConteosCarga;
